import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Alert, Spinner, Modal, Button } from 'react-bootstrap';
import { axiosPost } from '../../../../utils/axiosPost';
import { strapiUrl, CMSE_COMMITTEE_IDS } from '../../../../utils/constants';
import { chairFinalReportStatus } from '../../../../utils/chairFinalReportStatus';
import { useWordCount, WORD_LIMITS } from './hooks/useWordCount';
import useChairReportData from './hooks/useChairReportData';
import useChairReportForm from './hooks/useChairReportForm';
import useChairReportActions from './hooks/useChairReportActions';
import useChairReportPermissions from './hooks/useChairReportPermissions';
import useDeadlineInfo from './hooks/useDeadlineInfo';
import ReportTextSection from './ReportTextSection';
import MemberAttendanceTable from './MemberAttendanceTable';
import CandidateReportCard from './CandidateReportCard';
import ValidationSummary from './ValidationSummary';
import ReportActionButtons from './ReportActionButtons';
import ModalConfirmation from '../../ModalConfirmation';
import { devError } from '../../../../utils/devLog';

const HELP_TEXT = {
  gapAnalysis:
    'Please provide an indication of the gap (degree of separation) between candidates, and whether any are diversity candidates. Add any brief comments on the final short-list.',
  evaluationProcess:
    'Including instances of adjustments made to criterion weightings or consideration of candidates in their 5th year etc.',
  integrityMatters:
    'Please provide details of any concerns about any of the candidates (including the recommended candidates) that have arisen during the work of your committee about which you think Council should be aware. Please also comment on the disclosure process used in the 2026 round.',
  diversityIssues:
    "Please provide feedback on any diversity issues considered during your committee's consideration of the candidates, and how these were dealt with by the committee.",
  fnsFeedback:
    'Please provide any feedback on the Fellowship Nomination System and any suggestions for improvement.',
};

const ChairReport = ({
  committee,
  candidates,
  committeeMembers,
  chairName,
  isChair,
  isAdmin,
  canDownloadPdf,
  isReadOnly,
  electionTypeId,
  formatCandidate,
  showNotification,
  onReportSaved,
  isEditing = false,
  onRequestEdit,
  onDirtyChange,
}) => {
  const committeeId = Number(committee.sectionalCommitteeId || committee.id);
  const fieldPrefix = `${committeeId}-`;

  const {
    formData,
    setFormData,
    reportId,
    setReportId,
    reportStatus,
    setReportStatus,
    loading,
    deadline,
    refetchReport,
  } = useChairReportData({
    committeeId,
    candidates,
    committeeMembers,
    chairName,
    formatCandidate,
    electionTypeId,
  });

  const isSubmitted = reportStatus === 'submitted';

  const {
    isComplete,
    hasAnyOverLimit,
    getFieldStatus,
    getCandidateStatus,
    getCandidateExpertiseStatus,
    getAttendanceStatus,
  } = useWordCount(formData);

  const { isPastDeadline, deadlineInfo } = useDeadlineInfo(
    deadline,
    isSubmitted,
    formData,
    hasAnyOverLimit,
  );

  const { isEditable, canEdit } = useChairReportPermissions({
    isAdmin,
    isEditing,
    isReadOnly,
    isSubmitted,
    isPastDeadline,
    isChair,
  });

  const {
    validationErrors,
    setValidationErrors,
    warningErrors,
    setWarningErrors,
    isDirty,
    setIsDirty,
    handleFieldChange,
    handleAttendanceChange,
    handleCandidateReportChange,
    handleCandidateExpertiseChange,
    validateFilledFields,
    validateAllFields,
    getIncompleteFields,
    hasFieldError,
  } = useChairReportForm({
    formData,
    setFormData,
    getFieldStatus,
    getCandidateStatus,
    getCandidateExpertiseStatus,
    getAttendanceStatus,
    isEditable,
    isEditing,
    committeeId,
    onDirtyChange,
  });

  const {
    saving,
    submitting,
    showSubmitModal,
    setShowSubmitModal,
    validationSummaryRef,
    handleSaveDraft,
    handleSubmit,
    handleConfirmedSubmit,
  } = useChairReportActions({
    committeeId,
    committeeName: committee.sectionalCommitteeName,
    formData,
    reportId,
    setReportId,
    reportStatus,
    setReportStatus,
    isAdmin,
    isComplete,
    setIsDirty,
    setValidationErrors,
    setWarningErrors,
    validateFilledFields,
    validateAllFields,
    getIncompleteFields,
    showNotification,
    onReportSaved,
  });

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const response = await axiosPost('chair-report-pdf/generate', {
        data: { sectionalCommitteeId: committeeId },
      });
      if (response?.status === 200 && response.data?.url) {
        window.open(`${strapiUrl}${response.data.url}`, '_blank');
      }
    } catch (error) {
      devError('Failed to generate chair report PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [committeeId]);

  const [showLockWarningModal, setShowLockWarningModal] = useState(false);

  const handleLockClick = useCallback(() => {
    if (isEditing && isDirty) {
      setShowLockWarningModal(true);
      return;
    }
    onRequestEdit?.(committeeId);
  }, [isEditing, isDirty, onRequestEdit, committeeId]);

  const handleLockSave = useCallback(async () => {
    setShowLockWarningModal(false);
    const saved = await handleSaveDraft();
    if (saved) {
      onRequestEdit?.(committeeId);
    }
  }, [handleSaveDraft, onRequestEdit, committeeId]);

  const handleLockDiscard = useCallback(async () => {
    setShowLockWarningModal(false);
    await refetchReport({ silent: true });
    setIsDirty(false);
    setValidationErrors([]);
    setWarningErrors([]);
    onRequestEdit?.(committeeId);
  }, [
    refetchReport,
    setIsDirty,
    setValidationErrors,
    setWarningErrors,
    onRequestEdit,
    committeeId,
  ]);

  const saveAnchorRef = useRef(null);
  const formRef = useRef(null);
  const [isFloating, setIsFloating] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);

  useEffect(() => {
    if (!saveAnchorRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFloating(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(saveAnchorRef.current);
    return () => observer.disconnect();
  }, [loading, isEditable]);

  useEffect(() => {
    if (!formRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFormVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(formRef.current);
    return () => observer.disconnect();
  }, [loading]);

  const displayStatus = useMemo(
    () =>
      chairFinalReportStatus({
        reportId,
        formData,
        status: reportStatus,
      }),
    [reportId, formData, reportStatus],
  );

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Loading report form...</span>
      </div>
    );
  }

  return (
    <div ref={formRef} style={{ position: 'relative' }}>
      <Card
        className="rounded-0 border-0"
        style={{ borderTop: '1px solid rgba(0, 0, 0, 0.3) !important' }}
      >
        <Card.Header
          ref={validationSummaryRef}
          className="bg-white d-flex justify-content-center align-items-center"
        >
          <h6 className="mb-0 d-flex align-items-center">
            Chair&#x2019;s Final Report to Council{' '}
            <span
              className={`badge border border-${displayStatus.variant} text-${displayStatus.variant} ms-3`}
              style={{ backgroundColor: 'white' }}
            >
              {displayStatus.label}
            </span>
            {canEdit && (
              <button
                type="button"
                className={`btn btn-sm ms-3 ${isEditing ? 'btn-outline-danger' : 'btn-outline-secondary'}`}
                onClick={handleLockClick}
                title={
                  isEditing
                    ? 'Lock report (read-only)'
                    : 'Unlock report for editing'
                }
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
              >
                <i
                  className={`fas ${isEditing ? 'fa-lock-open' : 'fa-pencil-alt'} me-1`}
                />
                {isEditing ? 'Lock' : 'Edit'}
              </button>
            )}
            {canDownloadPdf && isSubmitted && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary ms-3"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                title="Download report as PDF"
                style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
              >
                <i
                  className={`fas ${isGeneratingPdf ? 'fa-sync fa-spin' : 'fa-file-pdf'} me-1`}
                />
                {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
              </button>
            )}
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="p-4 mx-auto" style={{ maxWidth: '900px' }}>
            {deadlineInfo && (
              <Alert variant={deadlineInfo.variant} className="mb-4">
                <i className={`fas ${deadlineInfo.icon} me-2`} />
                {deadlineInfo.message}
              </Alert>
            )}

            {isSubmitted && !isAdmin && (
              <Alert variant="success" className="mb-4">
                <i className="fas fa-check-circle me-2" />
                This report has been submitted to Council and is now read-only.
              </Alert>
            )}

            {isAdmin && isEditing && (
              <Alert variant="warning" className="mb-4">
                <i className="fas fa-user-shield me-2" />
                You are editing this report as an administrator.
              </Alert>
            )}

            <ValidationSummary
              errors={validationErrors}
              variant="danger"
              fieldPrefix={fieldPrefix}
            />

            <ValidationSummary
              errors={warningErrors}
              variant="warning"
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="Gap Between Candidates"
              field="gapAnalysis"
              helpText={HELP_TEXT.gapAnalysis}
              value={formData.gapAnalysis}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('gapAnalysis')}
              hasError={hasFieldError('gapAnalysis')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <MemberAttendanceTable
              members={formData.memberAttendance}
              isEditable={isEditable}
              hasFieldError={hasFieldError}
              getAttendanceStatus={getAttendanceStatus}
              onChange={handleAttendanceChange}
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="Evaluation Process"
              field="evaluationProcess"
              helpText={HELP_TEXT.evaluationProcess}
              value={formData.evaluationProcess}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('evaluationProcess')}
              hasError={hasFieldError('evaluationProcess')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="Integrity Matters & Disclosures"
              field="integrityMatters"
              helpText={HELP_TEXT.integrityMatters}
              value={formData.integrityMatters}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('integrityMatters')}
              hasError={hasFieldError('integrityMatters')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="Diversity Issues"
              field="diversityIssues"
              helpText={HELP_TEXT.diversityIssues}
              value={formData.diversityIssues}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('diversityIssues')}
              hasError={hasFieldError('diversityIssues')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="Fellowship Nomination System (FNS)"
              field="fnsFeedback"
              helpText={HELP_TEXT.fnsFeedback}
              value={formData.fnsFeedback}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('fnsFeedback')}
              hasError={hasFieldError('fnsFeedback')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <ReportTextSection
              label="General Observations / Feedback"
              field="generalObservations"
              value={formData.generalObservations}
              isEditable={isEditable}
              fieldStatus={getFieldStatus('generalObservations')}
              hasError={hasFieldError('generalObservations')}
              onChange={handleFieldChange}
              fieldPrefix={fieldPrefix}
            />

            <Form.Group className="mb-5">
              <Form.Label column={false} className="fw-bold">
                Reports on Ranked Candidates
              </Form.Label>
              {isEditable && (
                <Form.Text className="d-block text-muted mb-2">
                  Please write a report for each ranked candidate (
                  {WORD_LIMITS.candidateReport} words max).
                </Form.Text>
              )}

              {formData.candidateReports.length === 0 ? (
                <Alert variant="light" className="text-muted">
                  No ranked candidates to report on.
                </Alert>
              ) : (
                formData.candidateReports.map((candidateReport, index) => (
                  <CandidateReportCard
                    key={index}
                    candidateReport={candidateReport}
                    index={index}
                    isEditable={isEditable}
                    fieldStatus={getCandidateStatus(index)}
                    hasError={hasFieldError(`candidate-${index}`)}
                    onChange={handleCandidateReportChange}
                    showExpertise={CMSE_COMMITTEE_IDS.includes(committeeId)}
                    expertiseStatus={getCandidateExpertiseStatus(index)}
                    onExpertiseChange={handleCandidateExpertiseChange}
                    fieldPrefix={fieldPrefix}
                  />
                ))
              )}
            </Form.Group>

            {isEditable && (
              <ReportActionButtons
                ref={saveAnchorRef}
                showFloatingQuickSave={isFloating && isFormVisible}
                saving={saving}
                submitting={submitting}
                isComplete={isComplete}
                onSaveDraft={handleSaveDraft}
                onSubmit={handleSubmit}
                committeeName={committee.sectionalCommitteeName}
              />
            )}
          </div>
        </Card.Body>
      </Card>

      <ModalConfirmation
        showModal={showSubmitModal}
        setShowModal={setShowSubmitModal}
        title="Confirm Submission"
        handleCancelSelect={() => setShowSubmitModal(false)}
        bodyText={`Are you sure you want to **submit** this report to Council?<br/><br/>
Once submitted the report will be **locked** and you will not be able to make any further edits. To continue editing select **Cancel** and use the **Save Draft** button to save your changes.`}
        handleConfirmSelect={handleConfirmedSubmit}
      />

      <Modal
        show={showLockWarningModal}
        onHide={() => setShowLockWarningModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Would you like to save them before locking
          the report?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowLockWarningModal(false)}
          >
            Cancel
          </Button>
          <Button variant="outline-danger" onClick={handleLockDiscard}>
            Discard Changes
          </Button>
          <Button
            variant="success"
            className="standard-width-button"
            onClick={handleLockSave}
          >
            Save &amp; Lock
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

ChairReport.propTypes = {
  committee: PropTypes.shape({
    sectionalCommitteeId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectionalCommitteeName: PropTypes.string,
    electionTypeId: PropTypes.number,
    candidates: PropTypes.array,
  }).isRequired,
  candidates: PropTypes.array.isRequired,
  committeeMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      role: PropTypes.string,
    }),
  ).isRequired,
  chairName: PropTypes.string,
  isChair: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool,
  canDownloadPdf: PropTypes.bool,
  isReadOnly: PropTypes.bool.isRequired,
  electionTypeId: PropTypes.number.isRequired,
  formatCandidate: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  onReportSaved: PropTypes.func,
  isEditing: PropTypes.bool,
  onRequestEdit: PropTypes.func,
  onDirtyChange: PropTypes.func,
};

ChairReport.defaultProps = {
  chairName: null,
  isAdmin: false,
  canDownloadPdf: false,
  onReportSaved: null,
  isEditing: false,
  onRequestEdit: null,
  onDirtyChange: null,
};

export default ChairReport;
